import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import AuthGuard from '../../components/auth/AuthGuard';
import RoleGuard from '../../components/auth/RoleGuard';
import ErrorBoundary from '../../components/common/ErrorBoundary';
import DashboardRouter from '../DashboardRouter';
import LoginRedirect from '../LoginRedirect';
// Page components are code-split (lazy); <Suspense> lives in AuthenticatedRoutes.jsx.
const Courses = lazy(() => import('../../components/courses/Courses'));
const CourseDetail = lazy(() => import('../../components/courses/CourseDetail'));
const Electives = lazy(() => import('../../components/electives/Electives'));
const ElectiveDetail = lazy(() => import('../../components/electives/ElectiveDetail'));
const Assignments = lazy(() => import('../../components/assignments/Assignments'));
const Grades = lazy(() => import('../../components/grades/Grades'));
const Calendar = lazy(() => import('../../components/calendar/Calendar'));
const Students = lazy(() => import('../../components/students/Students'));
const Settings = lazy(() => import('../../components/settings/Settings'));
const Profile = lazy(() => import('../../components/profile/Profile'));
const AttendanceCheckIn = lazy(() => import('../../components/attendance/AttendanceCheckIn'));
const PaymentForm = lazy(() => import('../../components/payment/PaymentForm'));
const CourseCatalog = lazy(() => import('../../components/payment/CourseCatalog'));
const EnrollmentFlow = lazy(() => import('../../components/payment/EnrollmentFlow'));
const PurchaseConfirmation = lazy(() => import('../../components/payment/PurchaseConfirmation'));
const PaymentHistoryPage = lazy(() => import('../../components/payment/PaymentHistoryPage'));
const DonationForm = lazy(() => import('../../components/payment/DonationForm'));
const DonationConfirmation = lazy(() => import('../../components/payment/DonationConfirmation'));
const AssignmentSubmissions = lazy(() => import('../../pages/instructor/assignments/AssignmentSubmissions'));
const EditAssignment = lazy(() => import('../../pages/instructor/assignments/EditAssignment'));
const MyAttendance = lazy(() => import('../../pages/student/attendance/MyAttendance'));
const ReportIssue = lazy(() => import('../../pages/supportTickets/ReportIssue'));
const MySupportTickets = lazy(() => import('../../pages/supportTickets/MySupportTickets'));
const SupportTicketDetails = lazy(() => import('../../pages/supportTickets/SupportTicketDetails'));

// Routes with no admin/instructor/student prefix - login, enrollment/
// payment, dashboard, and the everyday authenticated pages. Extracted
// VERBATIM (same order) from AuthenticatedRoutes' route table.
const coreRoutes = [
  { path: '/login', element: <LoginRedirect /> },
  { path: '/attendance/checkin', element: <MyAttendance autoCheckIn /> },
  { path: '/attendance/checkin/:sessionId', element: <AttendanceCheckIn /> },
  { path: '/catalog', element: (
    <AuthGuard>
      <CourseCatalog />
    </AuthGuard>
  ) },
  { path: '/enroll/flow/:courseIds', element: <EnrollmentFlow /> },
  { path: '/enroll/products/:productIds', element: <EnrollmentFlow /> },
  { path: '/enroll/:courseId', element: (
    <AuthGuard>
      <ErrorBoundary>
        <PaymentForm />
      </ErrorBoundary>
    </AuthGuard>
  ) },
  { path: '/enroll/multiple/:courseIds', element: (
    <AuthGuard>
      <ErrorBoundary>
        <PaymentForm />
      </ErrorBoundary>
    </AuthGuard>
  ) },
  { path: '/purchase/products/:productIds', element: (
    <AuthGuard>
      <ErrorBoundary>
        <PaymentForm />
      </ErrorBoundary>
    </AuthGuard>
  ) },
  { path: '/confirmation', element: <PurchaseConfirmation /> },
  { path: '/dashboard', element: <DashboardRouter /> },
  { path: '/courses', element: <Courses /> },
  { path: '/courses/:courseId', element: <CourseDetail /> },
  { path: '/electives', element: <Electives /> },
  { path: '/electives/:id', element: <ElectiveDetail /> },
  { path: '/assignments', element: (
    <AuthGuard>
      <Assignments />
    </AuthGuard>
  ) },
  { path: '/assignments/:assignmentId/submissions', element: (
    <RoleGuard allowedRoles={['instructor', 'admin']}>
      <ErrorBoundary>
        <AssignmentSubmissions />
      </ErrorBoundary>
    </RoleGuard>
  ) },
  { path: '/assignments/:id/edit', element: (
    <RoleGuard allowedRoles={['instructor', 'admin']}>
      <EditAssignment />
    </RoleGuard>
  ) },
  { path: '/grades', element: (
    <AuthGuard>
      <Grades />
    </AuthGuard>
  ) },
  { path: '/attendance', element: (
    <AuthGuard>
      <MyAttendance />
    </AuthGuard>
  ) },
  { path: '/my-attendance', element: <Navigate to="/attendance" replace /> },
  { path: '/calendar', element: (
    <AuthGuard>
      <Calendar />
    </AuthGuard>
  ) },
  { path: '/students', element: (
    <AuthGuard>
      <Students />
    </AuthGuard>
  ) },
  { path: '/payment-history', element: (
    <AuthGuard>
      <PaymentHistoryPage />
    </AuthGuard>
  ) },
  { path: '/donation', element: <DonationForm /> },
  { path: '/donation/success', element: <DonationConfirmation /> },
  { path: '/profile', element: (
    <AuthGuard>
      <Profile />
    </AuthGuard>
  ) },
  { path: '/settings', element: (
    <AuthGuard>
      <Settings />
    </AuthGuard>
  ) },
  { path: '/support/report', element: (
    <RoleGuard allowedRoles={['student', 'instructor']}>
      <ReportIssue />
    </RoleGuard>
  ) },
  { path: '/support/my-tickets', element: (
    <RoleGuard allowedRoles={['student', 'instructor']}>
      <MySupportTickets />
    </RoleGuard>
  ) },
  { path: '/support/my-tickets/:ticketId', element: (
    <RoleGuard allowedRoles={['student', 'instructor']}>
      <SupportTicketDetails />
    </RoleGuard>
  ) },
];

export default coreRoutes;
